import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { API, errMsg } from "@/lib/api";

const T = "text";
const TA = "textarea";
const IMG = "image";
const BOOL = "bool";

const PAGES = [
  { key: "home", label: "Homepage", fields: [
    ["hero_heading", "Hero heading (new line = line break)", TA], ["hero_accent", "Hero accent line (script style)", T], ["hero_text", "Hero text", TA], ["hero_image", "Hero image", IMG],
    ["cta_label", "Button label", T], ["cta_link", "Button link (e.g. /shop)", T],
    ["secondary_cta_label", "Second button label", T], ["secondary_cta_link", "Second button link", T],
    ["feature_local_title", "Green band — left title", T], ["feature_local_text", "Green band — left text", TA],
    ["feature_delivery_title", "Green band — right title", T], ["feature_delivery_text", "Green band — right text", TA],
    ["promo_heading", "Promo band heading", T], ["promo_text", "Promo band text", TA],
    ["show_featured", "Show featured products", BOOL], ["show_categories", "Show occasion cards", BOOL],
    ["show_gallery", "Show gallery strip", BOOL], ["show_testimonials", "Show testimonials", BOOL],
    ["show_hg", "Show Heavenly Gems advert", BOOL], ["hg_heading", "Advert — heading", T], ["hg_text", "Advert — text", TA],
    ["hg_link", "Advert — link", T], ["hg_logo", "Advert — logo", IMG],
    ["seo_title", "SEO title", T], ["seo_description", "SEO description", TA],
  ]},
  { key: "about", label: "About Us", fields: [
    ["title", "Page title", T], ["body", "Main text (blank line = new paragraph)", TA], ["image", "Image", IMG],
    ["seo_title", "SEO title", T], ["seo_description", "SEO description", TA],
  ]},
  { key: "weddings_page", label: "Weddings", fields: [
    ["title", "Page title", T], ["intro", "Intro text", TA], ["body", "Main text", TA], ["image", "Image", IMG],
    ["cta_label", "Button label", T], ["seo_title", "SEO title", T], ["seo_description", "SEO description", TA],
  ]},
  { key: "funeral_page", label: "Funeral Tributes", fields: [
    ["title", "Page title", T], ["intro", "Intro text", TA], ["body", "Main text", TA], ["image", "Image", IMG],
    ["cta_label", "Button label", T], ["seo_title", "SEO title", T], ["seo_description", "SEO description", TA],
  ]},
  { key: "delivery_page", label: "Delivery", fields: [
    ["title", "Page title", T], ["body", "Main text", TA],
    ["seo_title", "SEO title", T], ["seo_description", "SEO description", TA],
  ]},
  { key: "contact_page", label: "Contact", fields: [
    ["title", "Page title", T], ["intro", "Intro text", TA],
    ["map_embed", "Google Maps embed code (iframe)", TA],
    ["seo_title", "SEO title", T], ["seo_description", "SEO description", TA],
  ]},
  { key: "footer", label: "Footer", fields: [
    ["text", "Footer text", TA], ["copyright", "Copyright name", T],
  ]},
];

function PageEditor({ page }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    API.get(`/admin/content/${page.key}`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [page.key]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...data };
      delete payload.key;
      await API.put(`/admin/content/${page.key}`, payload);
      toast.success(`${page.label} content saved`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (!data) return <Skeleton className="h-64 rounded-xl" />;

  return (
    <Card data-testid={`content-editor-${page.key}`}>
      <CardContent className="p-6 space-y-5">
        {page.fields.map(([key, label, type]) => (
          <div key={key} className="space-y-2">
            {type === BOOL ? (
              <div className="flex items-center justify-between">
                <Label>{label}</Label>
                <Switch checked={!!data[key]} onCheckedChange={(v) => setData({ ...data, [key]: v })} data-testid={`content-${page.key}-${key}`} />
              </div>
            ) : type === IMG ? (
              <>
                <Label>{label}</Label>
                <ImageUpload value={data[key] || ""} onChange={(v) => setData({ ...data, [key]: v })} label={label} testId={`content-${page.key}-${key}`} />
              </>
            ) : type === TA ? (
              <>
                <Label>{label}</Label>
                <Textarea rows={4} value={data[key] || ""} onChange={(e) => setData({ ...data, [key]: e.target.value })} data-testid={`content-${page.key}-${key}`} />
              </>
            ) : (
              <>
                <Label>{label}</Label>
                <Input value={data[key] || ""} onChange={(e) => setData({ ...data, [key]: e.target.value })} data-testid={`content-${page.key}-${key}`} />
              </>
            )}
          </div>
        ))}
        <Button className="rounded-full" onClick={save} disabled={saving} data-testid={`save-content-${page.key}`}>
          {saving ? "Saving…" : `Save ${page.label}`}
        </Button>
      </CardContent>
    </Card>
  );
}

export default function AdminContent() {
  return (
    <div data-testid="admin-content-page">
      <h1 className="font-display text-3xl tracking-tight mb-6">Website Content</h1>
      <p className="text-sm text-muted-foreground mb-6">Edit the text and images shown on each page of the website. Changes go live immediately.</p>
      <Tabs defaultValue="home">
        <TabsList className="flex-wrap h-auto" data-testid="content-tabs">
          {PAGES.map((p) => <TabsTrigger key={p.key} value={p.key} data-testid={`content-tab-${p.key}`}>{p.label}</TabsTrigger>)}
        </TabsList>
        {PAGES.map((p) => (
          <TabsContent key={p.key} value={p.key} className="mt-6">
            <PageEditor page={p} />
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
